/**
 * `<caos-key-value-row>` — one label/value pair for read-only detail.
 * (Catalog: Data display › KeyValueRow — one label/value pair for read-only detail.)
 *
 * A `label` (a mono uppercase kicker; attribute or `label` slot) beside its value (the
 * `value` attribute or the default slot). `stacked` puts the label above the value (the
 * Dragon field-group cell, laid into an auto-fit grid by the host). The value carries the
 * field-affordance look: when `interactive` is set it reads as an inline-editable /
 * linkable field — a dashed accent underline on hover that hugs the value (the Dragon
 * CrmField inline-edit idiom), never a boxed fill; a plain row stays static text. Every
 * colour/space reads a `--caos-*` token. Styling seams: `::part(row)`, `::part(label)`,
 * `::part(value)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { below } from '../breakpoints.js';

const sheet = makeSheet(`
  :host { display: block; }

  .row {
    display: grid;
    grid-template-columns: minmax(120px, 34%) 1fr;
    align-items: baseline;
    gap: var(--caos-space-2) var(--caos-space-4);
    padding: var(--caos-space-2) 0;
    border-bottom: 1px solid var(--caos-color-border);
  }
  :host([no-border]) .row { border-bottom: none; }

  /* Dragon kicker field label — a mono uppercase eyebrow, not an Inter caption. */
  .label {
    font-family: var(--caos-font-mono);
    font-size: 10px;
    font-weight: var(--caos-font-weight-label);
    letter-spacing: 0.22em;
    text-transform: uppercase;
    line-height: 1.4;
    color: var(--caos-color-text-muted);
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .value {
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    line-height: 1.5;
    color: var(--caos-color-text);
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .placeholder { color: var(--caos-color-text-muted); }
  :host(:not([empty])) .placeholder { display: none; }

  /* stacked variant — label above value (Dragon FieldGroupBlock cell); the host lays
     several of these into an auto-fit grid to build a field-group card. */
  :host([stacked]) .row {
    grid-template-columns: 1fr;
    align-items: start;
    gap: var(--caos-space-1) 0;
  }
  :host([stacked]) .value { margin-top: var(--caos-space-1); }

  /* field affordance — the Dragon inline-edit underline (CrmField P5b): a dashed accent
     underline that hugs the value, NOT a boxed surface fill. */
  :host([interactive]) .value {
    width: fit-content;
    max-width: 100%;
    cursor: pointer;
    border-bottom: 1px dashed transparent;
    transition: border-color 0.12s ease, color 0.12s ease;
  }
  :host([interactive]) .value:hover {
    border-bottom-color: var(--caos-color-accent);
    color: var(--caos-color-accent);
  }

  @media ${below('small')} {
    .row { grid-template-columns: 1fr; gap: 0; }
  }
`);

export class CaosKeyValueRow extends CaosElement {
  static get observedAttributes(): string[] {
    return ['label', 'value'];
  }

  private labelEl: HTMLDivElement;
  private valueEl: HTMLDivElement;
  private attrValue!: HTMLSpanElement;
  private valueSlot!: HTMLSlotElement;
  private placeholder!: HTMLSpanElement;

  constructor() {
    super(sheet);

    const row = document.createElement('div');
    row.className = 'row';
    row.setAttribute('part', 'row');

    this.labelEl = document.createElement('div');
    this.labelEl.className = 'label';
    this.labelEl.setAttribute('part', 'label');
    const labelSlot = document.createElement('slot');
    labelSlot.name = 'label';
    this.labelEl.append(labelSlot); // slotted label wins; else the attribute text

    this.valueEl = document.createElement('div');
    this.valueEl.className = 'value';
    this.valueEl.setAttribute('part', 'value');
    this.attrValue = document.createElement('span');
    this.valueSlot = document.createElement('slot'); // default slot = value
    this.placeholder = document.createElement('span');
    this.placeholder.className = 'placeholder';
    this.placeholder.setAttribute('aria-hidden', 'true');
    this.placeholder.textContent = '—'; // em dash for an empty value
    this.valueSlot.addEventListener('slotchange', () => this.reflectEmpty());
    this.valueEl.append(this.attrValue, this.valueSlot, this.placeholder);

    row.append(this.labelEl, this.valueEl);
    this.root.append(row);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    // Attribute label renders as a text node before the (optional) label slot.
    const labelText = this.getAttribute('label');
    this.labelEl.childNodes.forEach((n) => {
      if (n.nodeType === Node.TEXT_NODE) n.remove();
    });
    if (labelText) this.labelEl.insertBefore(document.createTextNode(labelText), this.labelEl.firstChild);

    // Attribute value (a shortcut); a default-slotted value takes precedence.
    this.attrValue.textContent = this.getAttribute('value') ?? '';
    this.reflectEmpty();
  }

  /** Toggle the em-dash placeholder when neither an attribute nor slotted value exists. */
  private reflectEmpty(): void {
    const hasSlotted = this.valueSlot.assignedNodes({ flatten: true }).some(
      (n) => n.nodeType !== Node.TEXT_NODE || (n.textContent ?? '').trim() !== '',
    );
    const hasAttr = (this.getAttribute('value') ?? '') !== '';
    if (hasSlotted || hasAttr) this.removeAttribute('empty');
    else this.setAttribute('empty', '');
  }
}

define('caos-key-value-row', CaosKeyValueRow);

declare global {
  interface HTMLElementTagNameMap {
    'caos-key-value-row': CaosKeyValueRow;
  }
}
